import React, { useState } from 'react';
import { 
  Ticket, 
  Search, 
  CheckCircle, 
  Clock, 
  XCircle, 
  RefreshCw, 
  ShieldCheck, 
  DollarSign, 
  User, 
  Building2, 
  Calendar,
  Eye,
  Filter
} from 'lucide-react';
import CyberBadge from '../common/CyberBadge';

export default function BookingsManagerView({
  bookings = [],
  payments = [],
  stats = null,
  onUpdateBookingStatus
}) {
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'confirmed', 'completed', 'refunded'
  const [searchTerm, setSearchTerm] = useState('');

  // Fallback demo bookings if database list is empty
  const displayBookings = bookings.length > 0 ? bookings : [
    {
      _id: 'bk-101',
      bookingStatus: 'confirmed',
      paymentStatus: 'paid',
      totalAmount: 499,
      createdAt: new Date().toISOString(),
      user: { name: 'Rahul Sharma', email: 'rahul.s@gmail.com' },
      room: { title: 'VIP Midnight Rooftop Warm Home Party' },
      bookingType: 'party_ticket',
      quantity: 2
    },
    {
      _id: 'bk-102',
      bookingStatus: 'confirmed',
      paymentStatus: 'paid',
      totalAmount: 299,
      createdAt: new Date(Date.now() - 3600000 * 5).toISOString(),
      user: { name: 'Aman Verma', email: 'aman.verma@gmail.com' },
      room: { title: 'Indore Secret 3 AM Cypher House' },
      bookingType: 'party_ticket',
      quantity: 1
    },
    {
      _id: 'bk-103',
      bookingStatus: 'completed',
      paymentStatus: 'paid',
      totalAmount: 1200,
      createdAt: new Date(Date.now() - 3600000 * 24).toISOString(),
      user: { name: 'Priya Patel', email: 'priya.p@yahoo.com' },
      room: { title: 'Indore Night Cricket Box Turf' },
      bookingType: 'ground_booking',
      quantity: 1
    }
  ];

  const filteredBookings = displayBookings.filter((b) => {
    const userName = b.user?.name || '';
    const userEmail = b.user?.email || '';
    const roomTitle = b.room?.title || '';
    const bookingId = b._id || b.id || '';

    const matchesSearch = !searchTerm.trim() ||
      userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      roomTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bookingId.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    if (activeTab === 'confirmed') return b.bookingStatus === 'confirmed';
    if (activeTab === 'completed') return b.bookingStatus === 'completed';
    if (activeTab === 'refunded') return b.bookingStatus === 'cancelled' || b.paymentStatus === 'refunded';

    return true;
  });

  const totalBookingsCount = displayBookings.length;
  const totalRevenue = displayBookings.reduce((acc, b) => acc + (parseFloat(b.totalAmount) || 0), 0);
  const confirmedCount = displayBookings.filter((b) => b.bookingStatus === 'confirmed').length;

  return (
    <div className="animate-fade" style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 className="page-title" style={{ fontSize: '1.75rem', fontWeight: 900, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Ticket size={28} style={{ color: 'var(--neon-green)' }} /> Party Ticket & Booking Escrow Manager
          </h1>
          <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Manage user ticket reservations, party bookings, escrow releases, and status overrides.
          </p>
        </div>
      </div>

      {/* Analytics Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
        <div className="cyber-card" style={{ padding: '20px', background: 'rgba(0, 255, 135, 0.1)', border: '1px solid rgba(0, 255, 135, 0.3)' }}>
          <div style={{ fontSize: '0.78rem', color: 'var(--neon-green)', fontWeight: 800, textTransform: 'uppercase' }}>Total Tickets & Bookings</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 900, color: '#FFFFFF', marginTop: '6px' }}>{totalBookingsCount}</div>
          <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: '4px' }}>Active platform reservations</div>
        </div>

        <div className="cyber-card" style={{ padding: '20px', background: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.3)' }}>
          <div style={{ fontSize: '0.78rem', color: '#A78BFA', fontWeight: 800, textTransform: 'uppercase' }}>Confirmed Pass Registrations</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 900, color: '#A78BFA', marginTop: '6px' }}>{confirmedCount}</div>
          <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: '4px' }}>Verified & Escrow Protected</div>
        </div>

        <div className="cyber-card" style={{ padding: '20px', background: 'rgba(0, 240, 255, 0.1)', border: '1px solid rgba(0, 240, 255, 0.3)' }}>
          <div style={{ fontSize: '0.78rem', color: 'var(--neon-cyan)', fontWeight: 800, textTransform: 'uppercase' }}>Total Ticket Gross</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 900, color: 'var(--neon-cyan)', marginTop: '6px' }}>₹{totalRevenue.toLocaleString()}</div>
          <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: '4px' }}>Escrow pool value</div>
        </div>
      </div>

      {/* Filter Tabs & Search */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', background: 'rgba(10,12,20,0.8)', padding: '12px 18px', borderRadius: '14px', border: '1px solid var(--border-color)' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          {[
            { id: 'all', label: `All Bookings (${displayBookings.length})` },
            { id: 'confirmed', label: `Confirmed (${confirmedCount})`, color: 'var(--neon-green)' },
            { id: 'completed', label: 'Completed' },
            { id: 'refunded', label: 'Cancelled / Refunded' },
          ].map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '0.82rem',
                  fontWeight: isActive ? 800 : 600,
                  background: isActive ? 'rgba(255,255,255,0.1)' : 'transparent',
                  border: isActive ? `1.5px solid ${tab.color || 'var(--neon-green)'}` : '1px solid transparent',
                  color: isActive ? '#FFFFFF' : 'var(--text-muted)',
                  cursor: 'pointer',
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <div style={{ position: 'relative', width: '280px' }}>
          <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            type="text"
            placeholder="Search by ticket ID, user, or party..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px 8px 36px',
              borderRadius: '8px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid var(--border-color)',
              color: '#FFFFFF',
              fontSize: '0.82rem',
              outline: 'none',
            }}
          />
        </div>
      </div>

      {/* Bookings Table */}
      <div className="cyber-table-container" style={{ background: 'rgba(10,12,20,0.8)', borderRadius: '16px', border: '1px solid var(--border-color)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.88rem' }}>
          <thead>
            <tr style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid var(--border-color)', color: 'var(--text-muted)', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              <th style={{ padding: '16px' }}>Ticket ID</th>
              <th style={{ padding: '16px' }}>Customer Details</th>
              <th style={{ padding: '16px' }}>Party / Event</th>
              <th style={{ padding: '16px' }}>Amount</th>
              <th style={{ padding: '16px' }}>Payment Status</th>
              <th style={{ padding: '16px' }}>Booking Status</th>
              <th style={{ padding: '16px', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredBookings.map((b) => (
              <tr key={b._id || b.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <td style={{ padding: '16px', fontWeight: 900, color: '#FFFFFF', fontFamily: 'var(--font-mono)' }}>
                  #{(b._id || b.id || '').slice(-6).toUpperCase()}
                </td>
                <td style={{ padding: '16px' }}>
                  <div style={{ fontWeight: 800, color: '#FFFFFF' }}>{b.user?.name || 'User Customer'}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{b.user?.email}</div>
                </td>
                <td style={{ padding: '16px' }}>
                  <div style={{ fontWeight: 800, color: '#A78BFA' }}>{b.room?.title || 'Warm Home Party'}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Qty: {b.quantity || 1} Pass</div>
                </td>
                <td style={{ padding: '16px', fontWeight: 900, color: 'var(--neon-green)', fontFamily: 'var(--font-mono)' }}>
                  ₹{b.totalAmount || b.amount || 0}
                </td>
                <td style={{ padding: '16px' }}>
                  <span className={`badge ${b.paymentStatus === 'paid' ? 'badge-green' : 'badge-purple'}`}>
                    {(b.paymentStatus || 'paid').toUpperCase()}
                  </span>
                </td>
                <td style={{ padding: '16px' }}>
                  <CyberBadge status={b.bookingStatus || 'confirmed'} />
                </td>
                <td style={{ padding: '16px', textAlign: 'right' }}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                    {b.bookingStatus !== 'completed' && onUpdateBookingStatus && (
                      <button
                        className="btn btn-outline"
                        style={{ padding: '6px 12px', fontSize: '0.75rem' }}
                        onClick={() => onUpdateBookingStatus(b._id || b.id, 'completed', 'paid')}
                      >
                        Complete
                      </button>
                    )}

                    {b.bookingStatus !== 'cancelled' && onUpdateBookingStatus && (
                      <button
                        className="btn btn-danger"
                        style={{ padding: '6px 12px', fontSize: '0.75rem' }}
                        onClick={() => onUpdateBookingStatus(b._id || b.id, 'cancelled', 'refunded')}
                      >
                        Cancel & Refund
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
